import { useState, useEffect, useCallback } from "react";

const TIMER_INTERVAL_MS = 60000; // 60 seconds

/**
 * Client-side hook that evaluates whether the current time is within the Display Period at 60-second intervals.
 * @param {string|null} startDateISO - Display period start datetime (ISO 8601 UTC)
 * @param {string|null} endDateISO - Display period end datetime (ISO 8601 UTC)
 * @returns {boolean} Whether the current time is within the display period
 */
const useDisplayPeriodTimer = (startDateISO, endDateISO) => {
  const evaluate = useCallback(() => {
    if (!startDateISO || !endDateISO) return false;
    const now = new Date();
    const start = new Date(startDateISO);
    const end = new Date(endDateISO);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;
    return now >= start && now <= end;
  }, [startDateISO, endDateISO]);

  const [isWithinPeriod, setIsWithinPeriod] = useState(() => evaluate());

  useEffect(() => {
    setIsWithinPeriod(evaluate());
    const intervalId = setInterval(() => {
      setIsWithinPeriod(evaluate());
    }, TIMER_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [evaluate]);

  return isWithinPeriod;
};

export default useDisplayPeriodTimer;
